import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useWindowSize from '@shared/hooks/useWindowsSize';
import { useTheme, useMediaQuery } from '@mui/material';
import { postMetaConverter } from '@shared/services/meta';
import { HOTELS } from '@shared/helpers/routes';
import { getSites } from '@shared/services/services';

import { filterAndPaginateHotels } from '../helpers';
import defaultImage from '@shared/assets/slides-noImg/no-hotel1.png';

import { orderHotelsById } from '@shared/helpers';

// Espera desde la última tecla antes de aplicar el filtro por nombre.
// Aplicarlo tecla por tecla implica volver a filtrar, ordenar y comparar la
// lista completa de hoteles por cada carácter, y con listas grandes eso
// bloquea la pantalla mientras se escribe.
const NAME_FILTER_DEBOUNCE_MS = 300;

export const useHotelSearchLogic = ({
  hotelSearchResults,
  total_passengers,
  totalNights,
}) => {
  const { id: id_site, business_types } = useSelector(
    store => store.pageConfiguration
  );
  const user = useSelector(store => store.userInfo);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSize = useWindowSize();
  const isMobile = screenSize.width < 1280;

  const aboutSection = useRef(null);

  // UI States
  const [showModal, setShowModal] = useState(false);
  const [hotel, setHotel] = useState();
  const [urlParams, setUrlParams] = useState({});

  const [hotelsWithPagination, setHotelsWithPagination] = useState([]);
  const [actualPage, setActualPage] = useState(1);

  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });

  const [pageQuantity, setPageQuantity] = useState(0);
  const [quantityFiltered, setQuantityFiltered] = useState(null);

  const [nameFilter, setNameFilter] = useState('');
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [pricesFiltered, setPricesFiltered] = useState([0, 0]);

  const [allFilteredHotels, setAllFilteredHotels] = useState([]);

  const [filter, setFilter] = useState('allOptions');
  const [orderHotelsBy, setOrderHotelsBy] = useState(0);
  const [viewFullWidhtMap, setViewFullWidhtMap] = useState(false);
  const [open, setOpen] = useState(false);

  const [isLoadImage, setIsLoadImage] = useState(false);

  const [sites, setSites] = useState([]);
  const [hoveredHotel, setHoveredHotel] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchSites = async () => {
      try {
        const result = await getSites();
        const resultWithAll = [
          { id: 0, name: 'Todos', value: 'selectAll' },
          ...result,
        ];
        if (isMounted) {
          setSites(resultWithAll.filter(x => x.id !== 4));
        }
      } catch (error) {
        if (isMounted) {
          console.error('error fetching sites', error);
        }
      }
    };

    fetchSites();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setFilter('allOptions');
    setOrderHotelsBy(0);
    setHoveredHotel(null);
    setViewFullWidhtMap(false);
    setNameFilter('');
    setPriceRange([0, 0]);
    setPricesFiltered([0, 0]);
    setActualPage(1);

    handleCloseModal();

    // Inicializamos el mapa
    const firstHotelWithCoords = hotelSearchResults.hotels.find(
      hotel =>
        hotel.location?.latitude !== null &&
        hotel.location?.latitude !== undefined
    );

    if (firstHotelWithCoords) {
      setMapCenter({
        lat: firstHotelWithCoords.location.latitude,
        lng: firstHotelWithCoords.location.longitude,
      });
    }

    if (!hotelSearchResults?.hotels?.length) {
      setAllFilteredHotels([]);
      setHotelsWithPagination([]);
      return;
    }

    const { filtered, hotels } = filterAndPaginateHotels({
      hotels: [...hotelSearchResults.hotels],
      pageSize: 18,
      pageNumber: 1,
      nameFilter: '',
      pricesFiltered: [0, 0],
      priceRange: [0, 0],
      totalNights,
      filter: 'allOptions',
      orderHotelsBy: 0,
      setQuantityFiltered,
      setPageQuantity,
    });

    setHotelsWithPagination(hotels);
    setAllFilteredHotels(filtered);
  }, [hotelSearchResults]);

  useEffect(() => {
    handleCardMouseEnter(null);
  }, [actualPage]);

  useEffect(() => {
    const { filtered, hotels } = getPaginatedHotels([
      ...hotelSearchResults.hotels,
    ]);
    setAllFilteredHotels(filtered);
    setHotelsWithPagination(hotels);
  }, [actualPage]);

  useEffect(() => {
    const sortHotels = orderHotelsById(orderHotelsBy, [...allFilteredHotels]);
    setAllFilteredHotels(sortHotels);
    setActualPage(1);
  }, [orderHotelsBy]);

  useEffect(() => {
    const isTooShortToFilter = nameFilter.length > 0 && nameFilter.length <= 2;
    if (isTooShortToFilter) return;

    // Al limpiar el filtro se aplica de una: no hay tipeo en curso que esperar.
    if (nameFilter.length === 0) {
      handelFilterAplicated();
      return;
    }

    const timeoutId = setTimeout(
      handelFilterAplicated,
      NAME_FILTER_DEBOUNCE_MS
    );
    return () => clearTimeout(timeoutId);
  }, [nameFilter]);

  const handelFilterAplicated = () => {
    const { filtered, hotels } = getPaginatedHotels([
      ...hotelSearchResults.hotels,
    ]);

    setAllFilteredHotels(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(filtered)) {
        return filtered;
      }
      return prev;
    });

    setHotelsWithPagination(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(hotels)) {
        return hotels;
      }
      return prev;
    });

    if (actualPage > 1) {
      setActualPage(1);
    }
  };

  const handleClearFilterAplicated = () => {
    setOrderHotelsBy(0);

    const { filtered, hotels } = filterAndPaginateHotels({
      hotels: [...hotelSearchResults.hotels],
      pageSize: 18,
      pageNumber: 1,
      nameFilter: '',
      pricesFiltered: [0, 0],
      priceRange: [0, 0],
      totalNights,
      filter: 'allOptions',
      orderHotelsBy: 0,
      setQuantityFiltered,
      setPageQuantity,
    });

    setHotelsWithPagination(hotels);
    setAllFilteredHotels(filtered);
    setActualPage(1);
  };

  const handleSetOrderHotelsBy = value => setOrderHotelsBy(value);

  const handleMapFullWidth = () => setViewFullWidhtMap(!viewFullWidhtMap);
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleCardMouseEnter = hotel => setHoveredHotel(hotel);
  const handleCardMouseLeave = () => setHoveredHotel(null);

  const handleModal = (viewModal, hotel) => {
    if (viewModal) {
      const tokenKey = `${hotel.__isFast ? 'fast' : 'normal'}-${
        hotel.id_integration
      }`;

      const token = hotelSearchResults?.tokenMap.get(tokenKey);

      if (!token) {
        console.warn(
          `[HotelSearchLogic] No se encontró token para hotel ${hotel.id_hotel} (id_integration: ${hotel.id_integration})`
        );
      }

      setUrlParams({
        token,
        integrationId: hotel.id_integration,
        hotelId: hotel.id_hotel,
        hotelCode: hotel.code,
        idCity: hotel.id_city,
        id_provider: hotel.id_provider || null,
      });
    }
    setHotel(hotel);
    setShowModal(viewModal);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsLoadImage(false);
    setHotel();
  };

  const handleChangePage = async () => {
    const searchFlowId = sessionStorage.getItem(
      `search-flow-id-${hotel?.__isFast ? 'fast' : 'normal'}`
    );
    if (searchFlowId) {
      sessionStorage.setItem('search-flow-id', searchFlowId);
    }

    const value = {
      id_itegracion: urlParams?.integrationId,
      id_proveedor: urlParams?.id_provider,
      id_city: urlParams?.idCity,
      id_hotel: urlParams?.hotelId,
      imagen: hotel?.main_image || defaultImage,
      nombre_hotel: hotel?.name,
    };
    window.dataLayer.push({
      event: 'verDetalleHotelsMapa',
      eventProps: {
        category: 'verDetalleHotelMapa',
        action: 'ver detalle',
        label: 'ver detalle',
        value,
      },
    });
    postMetaConverter(
      {
        event_name: 'verDetalleHotelsMapa',
        custom_data: {
          category: 'verDetalleHotelMapa',
          action: 'ver detalle',
          label: 'ver detalle',
          ...value,
        },
      },
      user
    );

    window.open(
      `${HOTELS}/detail?token=${urlParams.token}&integration=${urlParams.integrationId}&hotel=${urlParams.hotelId}&hotel_code=${urlParams.hotelCode}&city=${urlParams.idCity}&id_provider=${urlParams.id_provider}`
    );
  };

  const getPaginatedHotels = hotelList => {
    return filterAndPaginateHotels({
      hotels: hotelList,
      pageSize: 18,
      pageNumber: actualPage,
      nameFilter,
      pricesFiltered,
      priceRange,
      totalNights,
      filter,
      orderHotelsBy,
      setQuantityFiltered,
      setPageQuantity,
    });
  };

  return {
    // Datos
    aboutSection,
    fullScreen,
    isMobile,
    mapCenter,
    id_site,
    business_types,
    tokenMap: hotelSearchResults.tokenMap,
    total_passengers,
    totalNights,
    hoveredHotel,
    sites,

    // Estados de UI
    open,
    showModal,
    hotel,
    hotelsWithPagination,
    allFilteredHotels,
    pageQuantity,
    actualPage,
    quantityFiltered,
    viewFullWidhtMap,
    isLoadImage,

    // Filters & Ordering
    nameFilter,
    priceRange,
    pricesFiltered,
    filter,
    orderHotelsBy,

    // Setters & Handlers
    setActualPage,
    setNameFilter,
    setPriceRange,
    setPricesFiltered,
    setFilter,
    handleSetOrderHotelsBy,
    handleMapFullWidth,
    handleClickOpen,
    handleClose,
    handleModal,
    handleCloseModal,
    handleChangePage,
    handleCardMouseEnter,
    handleCardMouseLeave,
    handelFilterAplicated,
    handleClearFilterAplicated,
    setIsLoadImage,
  };
};
